/**
 * Saudi accounting defaults — SOCPA chart, ZATCA VAT, SAR currency.
 * Used by comptabilité shell pages and shared utilities.
 */
import {
  getBrandDefaultCurrency,
  getBrandVatRate,
  getBrandVatMultiplier,
  getBrandCurrencySuffix,
  getBrandLocale,
  formatBrandMoney,
} from './brandCurrency';

export const DEFAULT_CURRENCY = getBrandDefaultCurrency();
export const DEFAULT_VAT_RATE = getBrandVatRate();
export const DEFAULT_VAT_MULTIPLIER = getBrandVatMultiplier();
export const CURRENCY_SYMBOL = getBrandCurrencySuffix();
export const ACCOUNTING_LOCALE = getBrandLocale('ar');

/** Chart of accounts standard (Saudi SOCPA — not Algerian PCN/NSCF). */
export const DEFAULT_CHART_STANDARD = 'SOCPA';
export const LEGACY_CHART_STANDARDS = ['SOCPA', 'IFRS'];

/** Saudi VAT account prefixes (typical SOCPA mapping). */
export const VAT_ACCOUNTS = {
  outputVat: '230101', // ضريبة القيمة المضافة — مبيعات
  inputVat: '130101',  // ضريبة القيمة المضافة — مشتريات
  vatPayable: '230102',
  vatReceivable: '130102',
};

/** Zakat provision account (typical). */
export const ZAKAT_ACCOUNT = '230201';

/** Saudi tax declaration types (ZATCA). */
export const SAUDI_TAX_DECLARATION_TYPES = [
  { value: 'vat_return', label: 'إقرار ضريبة القيمة المضافة', code: 'VAT', isDefault: true },
  { value: 'zakat', label: 'إقرار الزكاة', code: 'ZAKAT', isDefault: true },
  { value: 'withholding', label: 'ضريبة الاستقطاع', code: 'WHT', isDefault: false },
  { value: 'corporate_income', label: 'ضريبة الدخل على الشركات', code: 'CIT', isDefault: false },
];

/** Saudi supplementary taxes (replaces Algerian IRA/TAP/FOPROLOS). */
export const SAUDI_ADDITIONAL_TAX_RULES = {
  zakat: { rate: 0.025, name: 'الزكاة' },
  withholding: { rate: 0.05, name: 'ضريبة الاستقطاع' },
  excise: { rate: 0.50, name: 'ضريبة انتقائية' },
  real_estate: { rate: 0.025, name: 'ضريبة التصرفات العقارية' },
};

/** SOCPA account class definitions (classes 1–7). */
export const SOCPA_CLASS_DEFINITIONS = {
  '1': { name: 'حقوق الملكية والالتزامات طويلة الأجل', nameAr: 'حقوق الملكية', color: '#3b82f6', type: 'capitaux' },
  '2': { name: 'الأصول غير المتداولة', nameAr: 'أصول غير متداولة', color: '#10b981', type: 'actif' },
  '3': { name: 'المخزون', nameAr: 'المخزون', color: '#f59e0b', type: 'actif' },
  '4': { name: 'الذمم المدينة والدائنة', nameAr: 'الذمم', color: '#ef4444', type: 'mixte' },
  '5': { name: 'النقد وما في حكمه', nameAr: 'النقد', color: '#8b5cf6', type: 'actif' },
  '6': { name: 'المصروفات', nameAr: 'المصروفات', color: '#dc2626', type: 'charge' },
  '7': { name: 'الإيرادات', nameAr: 'الإيرادات', color: '#059669', type: 'produit' },
};

export const formatAccountingAmount = (amount, showSymbol = true) => formatBrandMoney(amount, showSymbol);

/** Normalize legacy API currency/volume field names. */
export function normalizeCurrencyVolume(row) {
  if (!row || typeof row !== 'object') return row;
  const volume = row.volume_sar ?? row.volume_dzd ?? row.volume ?? 0;
  return {
    ...row,
    volume_sar: Number(volume) || 0,
    currency: row.currency || DEFAULT_CURRENCY,
  };
}

export default {
  DEFAULT_CURRENCY,
  DEFAULT_VAT_RATE,
  DEFAULT_VAT_MULTIPLIER,
  CURRENCY_SYMBOL,
  ACCOUNTING_LOCALE,
  DEFAULT_CHART_STANDARD,
  VAT_ACCOUNTS,
  ZAKAT_ACCOUNT,
  SAUDI_TAX_DECLARATION_TYPES,
  SAUDI_ADDITIONAL_TAX_RULES,
  SOCPA_CLASS_DEFINITIONS,
  formatAccountingAmount,
  normalizeCurrencyVolume,
};
